"use client";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";
import { Input } from "components/ui/v2/input";
import {
  fieldVariantClasses,
  type FieldVariant,
} from "components/ui/v2/field-variants";
import { Z_INDEX } from "components/ui/v2/z-index";
import { Check, ChevronDown, Search, X } from "lucide-react";
import * as React from "react";
import {
  findPickerOption,
  normalizePickerData,
  resolvePickerLabel,
  type PickerRawItem,
} from "./picker-data-utils";

export interface SelectPickerV2Props {
  data?: any[];
  items?: any[];
  value?: string | number | null;
  /** @deprecated rsuite compat — use value */
  defaultValue?: string | number | null;
  onChange?: (value: string | number | null, event?: React.SyntheticEvent) => void;
  labelKey?: string;
  valueKey?: string;
  placeholder?: React.ReactNode;
  searchable?: boolean;
  cleanable?: boolean;
  disabled?: boolean;
  loading?: boolean;
  block?: boolean;
  className?: string;
  style?: React.CSSProperties;
  menuStyle?: React.CSSProperties;
  menuMaxHeight?: number;
  variant?: FieldVariant;
  id?: string;
  name?: string;
  /** @deprecated rsuite compat — use onChange */
  onSelect?: (
    value: string | number | null,
    item?: PickerRawItem,
    event?: React.SyntheticEvent
  ) => void;
  /** @deprecated rsuite compat — ignored */
  creatable?: boolean;
  /** @deprecated rsuite compat — ignored */
  renderMenu?: (menu: React.ReactNode) => React.ReactNode;
  preventOverflow?: boolean;
  placement?: string;
  /** Tamaño del picker: "sm" | "filter" (w-64 h-11) | "md" | "lg". Default: "filter" */
  size?: "sm" | "filter" | "md" | "lg";
  menuAutoWidth?: boolean;
  menuClassName?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  /** @deprecated rsuite compat — ignored */
  onClean?: (event: React.SyntheticEvent) => void;
  /** @deprecated rsuite compat — use searchBy / internal filter */
  onSearch?: (searchKeyword: string, event?: React.SyntheticEvent) => void;
  renderMenuItem?: (
    label: React.ReactNode,
    item: any,
    selected?: boolean
  ) => React.ReactNode;
  renderValue?: (
    value: any,
    item: any,
    selectedElement: React.ReactNode
  ) => React.ReactNode;
  searchBy?: (
    keyword: string,
    label: React.ReactNode,
    item: any
  ) => boolean;
}

function normalize(value: string) {
  return value.trim().toLowerCase();
}

/**
 * Select nativo v2 — reemplaza el bridge rsuite SelectPicker.
 */
export const SelectPickerV2 = React.forwardRef<
  HTMLButtonElement,
  SelectPickerV2Props
>(function SelectPickerV2(
  {
    data,
    items,
    value,
    defaultValue,
    onChange,
    onSelect,
    renderMenu: _renderMenu,
    name: _name,
    menuAutoWidth: _menuAutoWidth,
    menuClassName: _menuClassName,
    id,
    onFocus,
    onBlur,
    onClean: _onClean,
    onSearch: _onSearch,
    labelKey = "label",
    valueKey = "value",
    placeholder = "Seleccionar...",
    searchable = true,
    cleanable = true,
    disabled = false,
    loading = false,
    block = false,
    className,
    style,
    menuStyle,
    menuMaxHeight = 256,
    variant = "slate",
    size = "filter",
    renderMenuItem,
    renderValue,
    searchBy,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedBy,
  },
  ref
) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState<
    string | number | null
  >(defaultValue ?? null);
  const colors = fieldVariantClasses(variant);

  const sizeClasses = size === "filter" ? "w-64 h-11" : "h-11";

  const rawData = data || items || [];

  const options = React.useMemo(
    () => normalizePickerData(rawData, labelKey, valueKey),
    [rawData, labelKey, valueKey]
  );

  const resolvedValue = isControlled ? value : (internalValue ?? defaultValue);

  const selectedOption = React.useMemo(
    () =>
      findPickerOption(
        options,
        resolvedValue == null ? null : String(resolvedValue)
      ),
    [options, resolvedValue]
  );

  const filteredOptions = React.useMemo(() => {
    const q = normalize(query);
    if (!q || !searchable) return options;
    return options.filter((option) => {
      if (searchBy) {
        return searchBy(q, option.label, option.raw);
      }
      return normalize(option.label).includes(q);
    });
  }, [options, query, searchable, searchBy]);

  const selectedLabel = React.useMemo(() => {
    if (!selectedOption) return null;
    return resolvePickerLabel(selectedOption, renderMenuItem, true);
  }, [renderMenuItem, selectedOption]);

  const triggerContent = React.useMemo(() => {
    if (loading) return String(placeholder);
    if (!selectedOption) return String(placeholder);

    const defaultElement = (
      <span className="min-w-0 flex-1 truncate">{selectedLabel}</span>
    );

    if (renderValue) {
      return renderValue(
        resolvedValue ?? null,
        selectedOption.raw,
        defaultElement
      );
    }

    return defaultElement;
  }, [
    loading,
    placeholder,
    renderValue,
    resolvedValue,
    selectedLabel,
    selectedOption,
  ]);

  const handleSelect = (nextValue: string) => {
    const selected = findPickerOption(options, nextValue);
    if (!isControlled) setInternalValue(nextValue);
    onChange?.(nextValue);
    onSelect?.(nextValue, selected?.raw, undefined);
    setOpen(false);
    setQuery("");
  };

  const handleClear = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isControlled) setInternalValue(null);
    onChange?.(null);
    onSelect?.(null, undefined, event);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery("");
  };

  const hasValue = selectedOption != null;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <div
        className={cn("relative my-1", sizeClasses, block && "w-full", className)}
        style={style}
      >
        <PopoverPrimitive.Trigger asChild disabled={disabled || loading}>
          <button
            ref={ref}
            type="button"
            id={id}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-haspopup="listbox"
            aria-expanded={open}
            onFocus={onFocus}
            onBlur={onBlur}
            className={cn(
              "relative flex items-center rounded-2xl border bg-white/70 text-left shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-200 outline-none",
              size === "filter" ? "w-64" : "w-full",
              "hover:bg-white/90 dark:bg-zinc-950/70 dark:shadow-[0_1px_2px_rgba(0,0,0,0.35)] dark:hover:bg-zinc-950/90",
              "focus-visible:ring-1 focus-visible:ring-offset-0",
              colors.border,
              colors.focusBorder,
              colors.focusRing,
              open && cn("ring-1 ring-offset-0", colors.focusRing.replace(/focus:/g, "")),
              size === "filter" ? "h-11 text-xs" : "h-11 text-sm",
              hasValue ? "px-4 pr-10" : "px-4 pr-9",
              (disabled || loading) && "cursor-not-allowed opacity-50",
              !hasValue && "text-muted-foreground"
            )}
          >
            {triggerContent}
            <ChevronDown
              className={cn(
                "pointer-events-none absolute right-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground transition-transform duration-200",
                open && "rotate-180"
              )}
            />
          </button>
        </PopoverPrimitive.Trigger>

        {hasValue && cleanable && !loading && !disabled ? (
          <button
            type="button"
            aria-label="Limpiar"
            onClick={handleClear}
            className="absolute right-8 top-1/2 z-10 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        ) : null}
      </div>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          side="bottom"
          sideOffset={4}
          collisionPadding={12}
          className={cn(
            "sp-panel z-[3000] w-[var(--radix-popover-trigger-width)] min-w-[14rem] overflow-hidden rounded-xl",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
            "duration-150"
          )}
          style={{ zIndex: Z_INDEX.MODAL + 100, ...menuStyle }}
        >
          {searchable ? (
            <div className="border-b border-border px-2 pt-2">
              <Input
                size="sm"
                variant={variant}
                label={String(placeholder)}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                leftIcon={<Search className="h-3.5 w-3.5" />}
              />
            </div>
          ) : null}

          <div
            className="overflow-y-auto overscroll-contain p-1"
            role="listbox"
            style={{ maxHeight: menuMaxHeight }}
          >
            {filteredOptions.length === 0 ? (
              <p className="px-2 py-4 text-center text-[11px] text-muted-foreground">
                Sin resultados
              </p>
            ) : (
              filteredOptions.map((option) => {
                const checked = selectedOption?.value === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={checked}
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] leading-tight transition-colors",
                      "hover:bg-muted/80",
                      checked && "bg-primary/8"
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border transition-colors",
                        checked
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-input bg-background"
                      )}
                    >
                      {checked ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : null}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-foreground">
                      {resolvePickerLabel(option, renderMenuItem, checked)}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>

      <style jsx global>{`
        .sp-panel {
          isolation: isolate;
          background:
            linear-gradient(
              to bottom,
              rgba(255, 255, 255, 0.96) 0%,
              rgba(255, 255, 255, 0.88) 100%
            ),
            rgba(248, 250, 252, 0.95);
          backdrop-filter: blur(16px) saturate(1.2);
          -webkit-backdrop-filter: blur(16px) saturate(1.2);
          box-shadow:
            inset 0 0 0 1px rgba(255, 255, 255, 0.6),
            0 4px 12px -2px rgba(15, 23, 42, 0.08),
            0 18px 40px -12px rgba(15, 23, 42, 0.14);
        }
        .dark .sp-panel {
          background:
            linear-gradient(
              to bottom,
              rgba(255, 255, 255, 0.08) 0%,
              rgba(255, 255, 255, 0.02) 100%
            ),
            rgba(20, 24, 40, 0.94);
          backdrop-filter: blur(16px) saturate(1.15);
          -webkit-backdrop-filter: blur(16px) saturate(1.15);
          box-shadow:
            inset 0 0 0 1px rgba(255, 255, 255, 0.1),
            0 8px 24px -4px rgba(0, 0, 0, 0.45);
        }
      `}</style>
    </PopoverPrimitive.Root>
  );
});

SelectPickerV2.displayName = "SelectPickerV2";
